/**
 * EXPO Proje To Do List soru sihirbazı — paylaşılan render bileşeni.
 *
 * Koşullu görünürlük: bir sorunun `condition`ı ({question_id, option_id})
 * doluysa, o soru yalnızca ilgili seçenek işaretliyken (ve üst zincir
 * görünürken) görünür. Alt dallar derinliğe göre girintili gösterilir.
 *
 * `answers` şekli: { [questionId]: { [optionId]: { checked, text } } }
 */
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";

/** Koşulun hedef seçenek id listesi; null = "Tümü" (herhangi bir seçenek).
 *  Eski tekil `option_id` biçimiyle geriye dönük uyumludur. */
export function condOptionIds(cond) {
  if (!cond) return null;
  if (Array.isArray(cond.option_ids))
    return cond.option_ids.length ? cond.option_ids : null;
  return cond.option_id ? [cond.option_id] : null;
}

export function isQuestionVisible(q, answers, questions = [], seen = new Set()) {
  if (!q?.condition || !q.condition.question_id) return true;
  if (seen.has(q.id)) return false;
  seen.add(q.id);
  const sel = answers[q.condition.question_id];
  const ids = condOptionIds(q.condition);
  if (ids === null) {
    const anyChecked =
      sel && Object.keys(sel).some((k) => sel[k]?.checked);
    if (!anyChecked) return false;
  } else if (!(sel && ids.some((id) => sel[id]?.checked))) {
    return false;
  }
  const parent = (questions || []).find((x) => x.id === q.condition.question_id);
  if (!parent) return true;
  return isQuestionVisible(parent, answers, questions, seen);
}

export function questionDepth(q, questions = [], seen = new Set()) {
  if (!q?.condition?.question_id || seen.has(q.id)) return 0;
  seen.add(q.id);
  const parent = (questions || []).find((x) => x.id === q.condition.question_id);
  if (!parent) return 0;
  return 1 + questionDepth(parent, questions, seen);
}

export function buildAnswersPayload(questions, answers) {
  return (questions || [])
    .filter((q) => isQuestionVisible(q, answers, questions))
    .map((q) => {
      const sel = answers[q.id] || {};
      const selections = (q.options || [])
        .filter((o) => sel[o.id]?.checked)
        .map((o) => ({
          option_id: o.id,
          text: (sel[o.id]?.text || "").trim() || null,
        }));
      return { question_id: q.id, selections };
    })
    .filter((a) => a.selections.length > 0);
}

/** Renk yuvarlağı + desen küçük resmi (varsa). */
function OptionVisual({ option }) {
  if (!option?.color && !option?.image) return null;
  return (
    <>
      {option.color && (
        <span
          className="inline-block h-4 w-4 shrink-0 rounded-full border border-zinc-300"
          style={{ backgroundColor: option.color }}
          title={option.color}
          data-testid={`wizard-color-${option.id}`}
        />
      )}
      {option.image && (
        <img
          src={option.image}
          alt=""
          className="h-7 w-7 shrink-0 rounded border border-zinc-200 object-cover"
          data-testid={`wizard-image-${option.id}`}
        />
      )}
    </>
  );
}

export default function QuestionWizard({ questions, answers, onChange }) {
  const toggle = (q, o) => {
    onChange((prev) => {
      const cur = { ...(prev[q.id] || {}) };
      const was = !!cur[o.id]?.checked;
      if (!q.multi) {
        Object.keys(cur).forEach((k) => {
          cur[k] = { ...cur[k], checked: false };
        });
      }
      cur[o.id] = { ...(cur[o.id] || {}), checked: !was };
      return { ...prev, [q.id]: cur };
    });
  };
  const setText = (q, o, text) =>
    onChange((prev) => ({
      ...prev,
      [q.id]: {
        ...(prev[q.id] || {}),
        [o.id]: { ...((prev[q.id] || {})[o.id] || {}), text },
      },
    }));
  // Dropdown: tek seçim — önceki seçimleri tamamen değiştirir.
  const selectOne = (q, optId) =>
    onChange((prev) => {
      const cur = {};
      if (optId) {
        const old = (prev[q.id] || {})[optId] || {};
        cur[optId] = { ...old, checked: true };
      }
      return { ...prev, [q.id]: cur };
    });

  const visible = (questions || []).filter((q) =>
    isQuestionVisible(q, answers, questions),
  );

  if (visible.length === 0)
    return (
      <div className="rounded-md border border-dashed border-zinc-200 px-3 py-6 text-center text-sm text-zinc-500">
        Görüntülenecek soru yok.
      </div>
    );

  return (
    <div className="space-y-5" data-testid="expo-question-wizard">
      {visible.map((q) => {
        const depth = Math.min(questionDepth(q, questions), 4);
        const isDropdown = q.display === "dropdown" && !q.multi;
        const st = answers[q.id] || {};
        const selectedId =
          Object.keys(st).find((k) => st[k]?.checked) || "";
        const selectedOpt = (q.options || []).find((o) => o.id === selectedId);
        return (
          <div
            key={q.id}
            data-testid={`wizard-question-${q.id}`}
            style={depth ? { marginLeft: depth * 16 } : undefined}
            className={depth ? "border-l-2 border-violet-200 pl-3" : undefined}
          >
            <div className="text-sm font-semibold text-zinc-900">{q.title}</div>
            {q.prompt && (
              <div className="mt-0.5 text-xs text-zinc-500">{q.prompt}</div>
            )}
            {isDropdown ? (
              <div className="mt-2 space-y-2">
                <div className="flex items-center gap-2">
                  <select
                    value={selectedId}
                    onChange={(e) => selectOne(q, e.target.value)}
                    className="h-9 w-full max-w-xs rounded-md border border-zinc-300 bg-white px-2 text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    data-testid={`wizard-dd-${q.id}`}
                  >
                    <option value="">Seçiniz…</option>
                    {(q.options || []).map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  {selectedOpt && <OptionVisual option={selectedOpt} />}
                </div>
                {selectedOpt?.has_text && (
                  <Input
                    value={st[selectedId]?.text || ""}
                    onChange={(e) => setText(q, selectedOpt, e.target.value)}
                    placeholder="Belirtiniz…"
                    className="h-7 w-40 text-sm"
                    data-testid={`wizard-text-${q.id}-${selectedId}`}
                  />
                )}
              </div>
            ) : (
              <div className="mt-2 space-y-1.5">
                {(q.options || []).map((o) => {
                  const os = st[o.id] || {};
                  return (
                    <div key={o.id} className="flex items-center gap-2">
                      <Checkbox
                        checked={!!os.checked}
                        onCheckedChange={() => toggle(q, o)}
                        data-testid={`wizard-opt-${q.id}-${o.id}`}
                      />
                      <OptionVisual option={o} />
                      <button
                        type="button"
                        onClick={() => toggle(q, o)}
                        className="text-sm text-zinc-700"
                      >
                        {o.label}
                      </button>
                      {o.has_text && os.checked && (
                        <Input
                          value={os.text || ""}
                          onChange={(e) => setText(q, o, e.target.value)}
                          placeholder="Belirtiniz…"
                          className="h-7 w-40 text-sm"
                          data-testid={`wizard-text-${q.id}-${o.id}`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
